import axios from 'axios';
import JSZip from 'jszip';
import { mutate } from 'swr';
import { GridSelectionModel } from '@mui/x-data-grid-pro';
import { withQueryParams } from '@shared/commonFunctions';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { getError } from '@shared/utils/helpers';
import { IContactFile, IContactFileResponse, IGenerateCustomPdfRequest, IStip, IStipFile } from './interfaces';

interface ISuccessType {
  isSuccess: boolean;
}

const path = `/${SERVICES.DOCUMENT}/stips`;
const lenderPath = `/${SERVICES.LENDER}`;
const dealPath = `/${SERVICES.DEAL}/deals`;

export const requestUploadStipFiles = (data: IStipFile) => {
  return mutate(path, async() => {
    try {
      // TODO: look for a usable method
      const formData: FormData = new FormData();
      formData.append('file', data.file as string | Blob);
      formData.append('assetId', data.assetId as string | Blob);
      formData.append('dealId', data.dealId as string | Blob);
      formData.append('contactId', data.contactId as string | Blob);
      formData.append('borrowerCoBorrowerType', data.borrowerCoBorrowerType as string | Blob);
      formData.append('stipTypeId', data.stipTypeId);

      const response = await axios.post(`${path}/fileUpload`, formData);
      let nextWorkflowStepName = '';
      if (response.data && response.data.cudlDocumentInfo) {
        nextWorkflowStepName = await axios.patch(`${dealPath}/${data.dealId}/process-cudl`, 
        {
          ...response.data.cudlDocumentInfo,
          documentId: response.data.id
        });
      }
      Notistack.successMessage(`File - ${data.file?.name} Uploaded Successfully`);

      return nextWorkflowStepName;
    } catch (err) {
      Notistack.generalError(getError(err));
    }

    return '';
  });
};

export const requestDownloadAllDealFiles = async (contactFiles: IContactFile[], fileName: string) => {
  const zip = new JSZip();
  const usedNames = new Set<string>();
  try {
    const downloadPromises = contactFiles.map(async (file) => {
      try {
        const stip = await requestGetStip(file.contactStipId, file.contactId);

        const response = await fetch(stip.blobDownloadLink);
        if (!response.ok) throw new Error(`Failed to fetch ${file.fileName}`);

        const blob = await response.blob();

        // Handle duplicate filenames within the ZIP
        let finalFileName = file.fileName;
        let counter = 1;
        while (usedNames.has(finalFileName)) {
          const extensionIndex = file.fileName.lastIndexOf('.');
          const base = file.fileName.substring(0, extensionIndex);
          const ext = file.fileName.substring(extensionIndex);
          finalFileName = `${base}_${counter}${ext}`;
          counter++;
        }
        usedNames.add(finalFileName);

        zip.file(finalFileName, blob);
      } catch (err) {
        Notistack.generalError(`Failed to download file ${file.fileName}`);
        console.error('Error adding file to zip:', err);
      }
    });

    await Promise.all(downloadPromises);

    const zipBlob = await zip.generateAsync({ type: 'blob' });

    // --- NATIVE DOWNLOAD START ---
    const url = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    // --- NATIVE DOWNLOAD END ---

    Notistack.successMessage('ZIP created successfully');
  } catch (error) {
    console.error('ZIP Generation failed:', error);
    Notistack.generalError();
  } 
};

export const requestDeleteStipFiles = (contactStipId: string, contactId: string, isFullDelete: boolean) => {
  const url = withQueryParams(`${path}/${contactStipId}`, { contactId, isFullDelete }, false);
  return mutate(url, async() => {
    try {
      await axios.delete(url);
      Notistack.successMessage('File - Deleted Successfully');
    } catch {
      Notistack.generalError();
    }
  });
};

export const requestGetStip = (contactStipId: string, contactId: string) => {
  const url = withQueryParams(`${path}/${contactStipId}`, { contactId , excludeFileBinaryContent: true}, false);
  return mutate(url, async() => {
    try {
      const response = await axios.get(url);
      Notistack.successMessage('File - Download Begins');
      return response.data;
    } catch {
      Notistack.generalError();
    }
  });
};

export const requestUpdateStipsReview = (data: IStip, contactStipId: string) => {
  // id and stipReviewRecordId
  const url = withQueryParams(
    `${path}/review/${contactStipId}`,
    { contactId: data.contactId, stipReviewStatus: data.reviewStatus },
    false,
  );
  return mutate(url, async() => {
    try {
      await axios.put(`${url}`, { ...data, contactStipId, id: data.stipReviewRecordId });
      Notistack.successMessage('Stips Review - Updated Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestUploadStips = (url: string, dealId: string, documentIds: GridSelectionModel) => {
  return mutate(lenderPath, async () => {
    try {
      const resp = await axios.post<ISuccessType>(url, { dealId, documentIds });
      if (!resp.data.isSuccess) {
        Notistack.generalError('Files Uploading Failed');
        return;
      }
      Notistack.successMessage('Files Successfully Uploaded');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestRestoreStipFiles = (contactStipId: string, contactId: string) => {
  const url = withQueryParams(`${path}/${contactStipId}`, { contactId }, false);
  return mutate(url, async() => {
    try {
      await axios.put(url);
      Notistack.successMessage('File - Restored Successfully');
    } catch {
      Notistack.generalError();
    }
  });
};

export const requestUpdateStipType = (contactStipId: string, contactId: string, stipTypeId: string, dealId: string, assetId: string) => {
  const url = withQueryParams(`${path}/${contactStipId}/stip-type`, { contactId, stipTypeId, dealId, assetId}, false);
  return mutate(url, async() => {
    try {
      await axios.patch(url, {});
      Notistack.successMessage('File - Type Updated Successfully');
    } catch {
      Notistack.generalError();
    }
  });
};

export const requestUploadStipsToDocuSign = (dealId: string, contactId: string, documentIds: GridSelectionModel) => {
  const url = `/${SERVICES.DOCUMENT}/esignature/documents/upload`;
  return mutate(url, async () => {
    try {
      const resp = await axios.post<ISuccessType>(url, { dealId, contactId, documentIds });
      if (!resp.data.isSuccess) {
        Notistack.generalError('Files Uploading Failed');
        return;
      }
      Notistack.successMessage('Files Successfully Uploaded');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestGenerateCustomPdf = (customPdfRequestData: IGenerateCustomPdfRequest) => {
	return mutate(path, async() => {
    try {
      const response = await axios.post(`${path}/generateCustomPdf`, customPdfRequestData);
      Notistack.successMessage('PDF File Successfully Created');
      return response;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestGetFiles = (contactId: string, dealId: string, assetId: string) => {
  const url = withQueryParams(`${SERVICES.DOCUMENT}/stips/contacts/${contactId}`, { dealId, assetId, includeDeleted: true }, false);

  return mutate(url, async () => {
    try {
      const response = await axios.get<IContactFileResponse>(url);

      return response?.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};
